<template>
  <b-modal
    id="modal-selector"
    :title="`Select ${
      dimType === 'sourceCube' ? 'source' : 'target'
    } for '${name}' Dimension`"
    @shown="openModal"
    @hide="resetModal"
    size="lg"
    no-close-on-backdrop
    scrollable
    centered
  >
    <b-overlay :show="showOverlay" no-wrap />

    <!-- Main type/hierarchy selectors -->
    <el-row :gutter="20">
      <el-col :span="12">
        <b-form-group label="Type" label-size="sm">
          <el-radio-group
            v-model="mappingType"
            style="width: 100%"
            size="small"
            @change="resetSel"
          >
            <el-radio-button label="Element(s)" class="fw"></el-radio-button>
            <el-radio-button label="Subset" class="fw"></el-radio-button>
            <el-radio-button
              label="Attribute"
              class="fw"
              :disabled="['source', 'sourceCube'].includes(dimType)"
            ></el-radio-button>
          </el-radio-group>
        </b-form-group>
      </el-col>
      <el-col :span="12">
        <b-form-group label="Hierarchy" label-size="sm">
          <b-form-select
            v-model="hierarchy"
            size="sm"
            :options="hierarchies"
            :disabled="hierarchies.length === 1"
            @change="hierarchyChanged"
          >
          </b-form-select>
        </b-form-group>
      </el-col>
    </el-row>
    <!-- selectors for subset and alias (if element type) -->
    <el-row :gutter="20" v-if="mappingType === 'Element(s)'">
      <el-col :span="12">
        <b-form-group label="Subset" label-size="sm">
          <b-form-select
            v-model="subset"
            size="sm"
            :options="subsets"
            :disabled="subsets.length === 0"
            @change="loadElements"
          >
            <template v-slot:first>
              <b-form-select-option :value="null"
                >All Elements (No Subset/Default)</b-form-select-option
              >
            </template>
          </b-form-select>
        </b-form-group>
      </el-col>
      <el-col :span="12">
        <b-form-group label="Alias" label-size="sm">
          <b-form-select
            v-model="alias"
            size="sm"
            :options="aliases"
            :disabled="aliases.length === 0"
            @change="aliasChanged"
          >
            <template v-slot:first>
              <b-form-select-option :value="null"
                >Element Name</b-form-select-option
              >
            </template>
          </b-form-select>
        </b-form-group>
      </el-col>
    </el-row>
    <!-- selector for attribute (if Attribute Type) -->
    <el-row :gutter="20" v-if="mappingType === 'Attribute'">
      <el-col :span="24">
        <b-form-group label="Attribute" label-size="sm">
          <b-form-select
            v-model="attribute"
            size="sm"
            :options="attributes"
            :disabled="attributes.length === 0"
            @change="attributeSelected"
          >
            <template v-slot:first>
              <b-form-select-option :value="null" disabled
                >Select an attribute</b-form-select-option
              >
            </template>
          </b-form-select>
        </b-form-group>
      </el-col>
    </el-row>
    <!-- selector for Subset (if Subset Type) -->
    <el-row :gutter="20" v-if="mappingType === 'Subset'">
      <el-col :span="24">
        <b-form-group label="Subset" label-size="sm">
          <b-form-select
            v-model="subset"
            size="sm"
            :options="subsets"
            :disabled="subsets.length === 0"
            @change="subsetSelected"
          >
            <template v-slot:first>
              <b-form-select-option :value="null" disabled
                >Select a subset</b-form-select-option
              >
            </template>
          </b-form-select>
        </b-form-group>
      </el-col>
    </el-row>
    <!-- Search bar -->
    <b-form @submit.prevent="loadElements" v-if="mappingType === 'Element(s)'">
      <b-form-group label-size="sm">
        <span slot="label">
          Search
          <small>
            <i
              class="el-icon-info"
              style="position: relative; left: 2px; bottom: 4px"
              v-b-popover.hover.right="'Element search is case-sensitive.'"
            />
          </small>
        </span>
        <b-input-group size="sm">
          <b-form-input v-model="search" type="search" @update="loadElements" />
          <b-input-group-append>
            <b-button
              :disabled="!search"
              @click="searchDimElements"
              type="submit"
            >
              <b-icon icon="search" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <!-- Add new element buttons -->
      <b-button-group
        style="float: right"
        v-if="search === '' && subset === null"
      >
        <el-tooltip placement="top" content="Create Root Element">
          <b-button
            size="sm"
            id="create-element"
            @click="createElement(false)"
            style="border-color: white; outline: none"
          >
            <b-icon icon="plus-circle" />
          </b-button>
        </el-tooltip>
        <el-tooltip placement="bottom" content="Create Child Element">
          <b-button
            size="sm"
            id="create-element"
            @click="createElement(true)"
            style="border-color: white; outline: none"
            :disabled="!selection"
          >
            <b-icon icon="node-plus" />
          </b-button>
        </el-tooltip>
      </b-button-group>
      <!-- Element Tree -->
      <b-form-group label="Elements" label-size="sm">
        <v-jstree
          v-if="refresh"
          :data="elements"
          :async="expandElement"
          show-checkbox
          multiple
          allow-batch
          text-field-name="name"
          @item-click="elementSelected"
        />
        <!-- <el-tree
          :data="elements"
          :props="{ label: 'name' }"
          show-checkbox
          :render-content="renderContent"
          lazy
          :load="loadChildren"
        /> -->
      </b-form-group>
    </b-form>
    <!-- Modal footer -->
    <template v-slot:modal-footer>
      <el-button
        variant="secondary"
        class="cancel-button float-right"
        @click="showPreview"
        v-if="
          taskType !== 'Zero Out' &&
          ['target', 'targetCube'].includes(dimType) &&
          ['Multi-Select', 'Subset', 'Consolidated', 'Attribute'].includes(
            selectionType
          )
        "
      >
        Preview Mapping
      </el-button>
      <!-- <el-button variant="primary" class="button float-right"> -->
      <el-button variant="primary" class="button float-right" @click="handleOk">
        OK
      </el-button>
    </template>
  </b-modal>
</template>

<script>
import { mapState } from "vuex";
import { required } from "vuelidate/lib/validators";
import TaskService from "../../services/TaskService";
import Helper from "../../utilities/Helper";

export default {
  props: {
    taskType: String,
    placeholder: Object,
    dim: Object,
    dimIndex: Number,
    dimType: String,
    taskSourceType: String,
    sourceElsIn: Array,
    targetElsIn: Array,
  },
  watch: {
    hierarchies(hierarchies) {
      this.hierarchies = hierarchies;
      this.hierarchy = hierarchies[0];
    },
    hierarchy(hierarchy) {
      this.hierarchy = hierarchy;
    },
    dim(value) {
      this.name = value.name;
      // this.elements = value.elements;
      this.aliases = value.aliases;
      this.alias = value.selectionAlias;
      if (value.selectionType === "Subset") this.subset = value.selection;
      if (value.selectionType === "Attribute") this.attribute = value.selection;
      this.mappingType =
        value.selectionType === "Subset"
          ? "Subset"
          : this.selectionType === "Attribute"
          ? "Attribute"
          : "Element(s)";
      if (this.type === "targetCube")
        this.mappingType =
          value.selectionType === "Subset"
            ? "Subset"
            : value.selectionType === "Attribute"
            ? "Attribute"
            : "Element(s)";
    },
    dimIndex(value) {
      this.index = value;
    },
    dimType(value) {
      this.type = value;
    },
  },
  computed: {
    ...mapState(["instanceId"]),
  },

  data() {
    return {
      name: this.dim.name,
      type: this.dimType,

      hierarchies: [],
      hierarchy: this.dim.name,

      subsets: [],
      subset: this.dim.selectionType === "Subset" ? this.dim.selection : null,

      aliases: this.dim.aliases,
      alias: this.dim.selectionAlias,

      attributes: [],
      attribute: null,

      selection: null,
      selectionType: null,

      elements: [],

      search: "",
      mappingType: "Element(s)",

      showOverlay: true,

      index: this.dimIndex,

      refresh: true,
    };
  },

  methods: {
    handleError: Helper.handleError,

    /**
     * Adds a new element to the dimension
     * @function
     */
    async createElement(createAsChild) {
      //Temporarily disable adding a child to base-level element
      if (createAsChild && this.selectionType !== "Consolidated") {
        alert(
          `Adding a child element to your current selection is not supported. If you'd like to add an element from within DataMaestro, please choose a single consolidated element. If you'd like add an element to your current selection, you can do so using the Planning Analytics administration tools.`
        );
        return;
      }
      const message = createAsChild
        ? `The new element will be added as a child of "${this.selection}".`
        : "The new element will be added to the root of the dimension's default hierarchy.";
      const input = prompt(
        "Enter the name of the numeric element and click OK to submit. " +
          message
      );
      if (input === null) {
        return;
      } else if (input) {
        const loading = this.$loading({
          lock: true,
          //text: "Working",
          background: "rgba(0, 0, 0, 0.3)",
        });
        try {
          await TaskService.createElement({
            instanceId: this.instanceId,
            dimName: this.name,
            element: input,
            type: "Numeric", //TEMP: hard code element type to Numeric
            parent: createAsChild ? this.selection : null,
          });

          this.$bvModal.hide("modal-selector");
          this.resetModal();
          this.$bvModal.show("modal-selector");
        } catch (err) {
          this.$bvModal.hide("modal-selector");
          this.resetModal();
          this.$bvModal.show("modal-selector");
          this.handleError(err, this, this.instanceId);
        } finally {
          loading.close();
        }
      } else {
        this.$message({
          type: "warning",
          message: "You must provide valid input to create a new element.",
          duration: 7000,
          showClose: true,
        });
      }
      // }
    },

    resetSel() {
      this.selection = null;
      this.selectionType = null;
      if (this.subset && this.mappingType === "Subset") {
        this.selection = this.subset;
        this.selectionType = "Subset";
      }
      if (this.attribute && this.mappingType === "Attribute") {
        this.selection = this.attribute;
        this.selectionType = "Attribute";
      }
    },

    attributeSelected() {
      this.selection = this.attribute;
      this.selectionType = "Attribute";
    },

    async showPreview() {
      this.$emit("showMappingPreview", {
        index: this.index,
        targetDim: {
          name: this.name,
          selection: this.selection,
          hierarchy: this.hierarchy,
          selectionType: this.selectionType,
          selectionAlias: this.alias,
        },
      });
    },

    resetModal() {
      this.selection = null;
      this.selectionType = null;
      this.checkValid = false;
      this.search = "";
      this.searchElements = [];
      this.alias = this.dim.selectionAlias;
      this.subset = null;
      this.mappingType = "Element(s)";
      this.hierarchy = null;
    },

    async openModal() {
      this.showOverlay = true;

      // load stuff...
      await this.loadHierarchies();
      await Promise.all([this.loadAttributes(), this.loadSubsets()]);
      // await this.loadElements();

      this.showOverlay = false;
    },

    async searchDimElements() {
      try {
        this.showOverlay = true;
        this.selection = null;
        this.selectionType = null;
      } catch (err) {
        this.$bvModal.hide("modal-selector");
        this.resetModal();
        this.handleError(err, this, this.instanceId);
      } finally {
        this.showOverlay = false;
      }
    },

    async loadElements() {
      try {
        this.refresh = false;
        this.$nextTick(() => {
          this.refresh = true;
        });
      } catch (err) {
        this.$bvModal.hide("modal-selector");
        this.resetModal();
        this.handleError(err, this, this.instanceId);
      } finally {
        this.$nextTick(() => {
          this.showOverlay = false;
        });
      }
    },

    async loadHierarchies() {
      const response = await TaskService.getHierarchies({
        instanceId: this.instanceId,
        dimName: this.name,
      });
      this.hierarchies = response.data.hierarchies.value.map(
        (hierarchy) => hierarchy.Name
      );
    },

    async hierarchyChanged() {
      this.showOverlay = true;
      await this.loadSubsets();
      this.subset = null;
      await this.loadElements();
      this.showOverlay = false;
    },

    async aliasChanged() {
      this.showOverlay = true;
      await this.loadElements();
      this.showOverlay = false;
    },

    async loadSubsets() {
      const response = await TaskService.getSubsets({
        instanceId: this.instanceId,
        dimName: this.name,
        hierarchy: this.hierarchy,
      });
      this.subsets = response.data.hierarchies.Subsets.map(
        (subset) => subset.Name
      );
    },

    async loadAttributes() {
      const response = await TaskService.getAttributes({
        instanceId: this.instanceId,
        dimName: this.name,
        hierarchy: this.hierarchy,
      });
      this.attributes = response.data.hierarchies.value.map(
        (attribute) => attribute.Name
      );
    },

    async expandElement(node, resolve) {
      try {
        this.showOverlay = true;

        // this.selection = null;
        // this.selectionType = null;

        let data = [];
        let count = 0;

        if (!node.data.id) {
          while (this.elements.length > 0) {
            this.elements.pop();
          }

          if (this.subset && this.search) {
            // get subset search
          } else if (this.subset) {
            data = [
              {
                name: this.subset,
                type: "Subset",
                isLeaf: false,
                opened: true,
                disabled: true,
              },
            ];
          } else if (this.search) {
            let response = await TaskService.searchElements({
              instanceId: this.instanceId,
              dimName: this.name,
              searchValue: this.search,
              alias: !this.alias ? "Caption" : this.alias,
              skipCount: 0,
            });
            data = response.data.elements;
            count = response.data.count;
            if (count === 0) {
              data = [{ name: "No Results", isLeaf: true, disabled: true }];
            }
          } else {
            // get root elements
            let response = await TaskService.getDimRootElements({
              instanceId: this.instanceId,
              hierarchy: this.hierarchy ?? this.name,
              dimName: this.name,
              skipCount: 0,
            });
            if (response.data.elements.length === 0) {
              throw new Error("This dimension does not contain any elements.");
            } else {
              data = response.data.elements;
              count = response.data.count;
            }
          }
        } else {
          if (node.data.type === "Subset") {
            let response = await TaskService.getSubsetElements({
              instanceId: this.instanceId,
              dimName: this.name,
              hierarchy: this.hierarchy,
              subset: this.subset,
            });
            data = response.data.elements.Elements;
          } else {
            const childResponse = await TaskService.getElementChildren({
              instanceId: this.instanceId,
              hierarchy: this.hierarchy ?? this.name,
              dimName: this.name,
              elementName: node.data.defaultName,
              skipCount: 0,
            });
            data = childResponse.data.children;
            count = childResponse.data.count;
          }
        }
        if (this.alias) {
          data.forEach((element) => {
            element.name = element?.attributes?.[this.alias] ?? element.name;
          });
        }

        if (node.data.selected)
          data.forEach((element) => {
            element.disabled = true;
            element.selected = true;
          });

        if (data.length < count) {
          data.push({
            name: "Load More",
            isLeaf: true,
            icon: "fa fa-angle-double-down",
          });
        }
        resolve(data);
      } catch (e) {
        this.$bvModal.hide("modal-selector");
        this.resetModal();
        this.handleError(e, this, this.instanceId);
      } finally {
        this.showOverlay = false;
      }
    },

    // async expandElementOLD(node, resolve) {
    //   try {
    //     //show loading
    //     this.showOverlay = true;

    //     // intialize data and count
    //     let data = [];
    //     let count = 0;

    //     if (!node.data.id) {
    //       while (this.elements.length > 0) {
    //         this.elements.pop();
    //       }

    //       let rootResponse;

    //       if (this.subset) {
    //         rootResponse = await this.getSubsetElements();
    //       } else {
    //         rootResponse = await TaskService.getDimRootElements({
    //           instanceId: this.instanceId,
    //           hierarchy: this.hierarchy ?? this.name,
    //           dimName: this.name,
    //           skipCount: 0,
    //         });
    //       }

    //       if (rootResponse.data.elements.length === 0) {
    //         throw new Error("This dimension does not contain any elements.");
    //       } else {
    //         data = rootResponse.data.elements;
    //         count = rootResponse.data.count;
    //       }
    //     } else {
    //       const childResponse = await TaskService.getElementChildren({
    //         instanceId: this.instanceId,
    //         hierarchy: this.hierarchy ?? this.name,
    //         dimName: this.name,
    //         elementName: node.data.defaultName,
    //         skipCount: 0,
    //       });
    //       data = childResponse.data.children;
    //       count = childResponse.data.count;
    //     }

    //     if (this.alias) {
    //       data.forEach((element) => {
    //         element.name = element.attributes[this.alias];
    //       });
    //     }

    //     if (node.data.selected)
    //       data.forEach((element) => {
    //         element.disabled = true;
    //         element.selected = true;
    //       });

    //     if (data.length < count) {
    //       data.push({
    //         name: "Load More",
    //         isLeaf: true,
    //         icon: "fa fa-angle-double-down",
    //       });
    //     }
    //     resolve(data);
    //   } catch (err) {
    //     this.$bvModal.hide("modal-selector");
    //     this.resetModal();
    //     this.handleError(err, this, this.instanceId);
    //   } finally {
    //     this.showOverlay = false;
    //   }
    // },
    async elementSelected(node) {
      try {
        this.showOverlay = true;

        if (node.data.icon === "fa fa-angle-double-down") {
          if (node.$parent.data.icon === "") {
            // TODO: this is a bad way to check if it's a search
            // Load more search results
            const response = await TaskService.searchElements({
              instanceId: this.instanceId,
              dimName: this.name,
              hierarchy: this.hierarchy ?? this.name,
              searchValue: this.search,
              alias: !this.alias ? "Caption" : this.alias,
              skipCount: this.searchElements[0].children.length - 1,
            });
            const data = response.data.elements;
            data.forEach((element) => node.data.addBefore(element, node));
            const updatedCount = this.searchElements[0].children.length - 1;
            if (response.data.count === updatedCount) {
              this.searchElements[0].children.splice(updatedCount, 1);
            } else {
              node.data.selected = false;
            }
          } else if (!node.$parent.data.children) {
            // Load more roots
            const response = await TaskService.getDimRootElements({
              instanceId: this.instanceId,
              dimName: this.name,
              hierarchy: this.hierarchy ?? this.name,
              skipCount: this.elements.length - 1,
            });
            const data = response.data.elements;
            if (this.alias) {
              data.forEach((element) => {
                element.name = element.attributes[this.alias];
              });
            }

            data.forEach(async (element) => {
              if (!element.isLeaf) {
                element.children = [
                  {
                    name: "Loading...",
                    disabled: true,
                    loading: true,
                  },
                ];
              }
              node.data.addBefore(element, node);
            });
            const updatedCount = this.elements.length - 1;
            if (response.data.count === updatedCount) {
              this.elements.splice(updatedCount, 1);
            } else {
              node.data.selected = false;
            }
          } else {
            // Load more children
            const childrenCount = node.$parent.data.children.length - 1;
            const response = await TaskService.getElementChildren({
              instanceId: this.instanceId,
              dimName: this.name,
              hierarchy: this.hierarchy ?? this.name,
              elementName: node.$parent.data.defaultName,
              skipCount: childrenCount,
            });
            const data = response.data.children;
            if (this.alias) {
              data.forEach((element) => {
                element.name = element.attributes[this.alias];
              });
            }
            data.forEach((child) => {
              if (node?.data.selected && child?.model)
                child.model.disabled = true;
              if (!child.isLeaf) {
                child.children = [
                  {
                    name: "Loading...",
                    disabled: true,
                    loading: true,
                  },
                ];
              }
              node.data.addBefore(child, node);
            });
            const updatedChildrenCount = node.$parent.data.children.length - 1;
            if (response.data.count === updatedChildrenCount) {
              node.$parent.data.children.splice(updatedChildrenCount, 1);
            } else {
              node.data.selected = false;
            }
          }
        } else {
          // get current selection
          let selectedElements = !this.selection
            ? []
            : this.selectionType === "Multi-Select"
            ? this.selection
            : [{ name: this.selection, type: this.selectionType }];

          // adjust for new selection
          if (node.data.selected) {
            // add selected element
            selectedElements.push({
              name: node.data.name,
              type: node.data.type,
            });
          } else {
            // remove deselected element
            selectedElements = selectedElements.filter(
              (element) => element.name !== node.data.name
            );
          }

          // check/uncheck and enable/disable element children
          selectedElements = (function fixChildren(node, enable, values) {
            node.$children.forEach((child) => {
              child.model.disabled = enable;
              child.model.selected = enable;
              values = fixChildren(
                child,
                enable,
                values.filter((value) => value.name !== child.model.name)
              );
            });
            return values;
          })(node, node.data.selected, selectedElements);

          // set selection
          if (selectedElements.length === 0) {
            this.selection = null;
            this.selectionType = null;
          } else if (selectedElements.length === 1) {
            this.selection = selectedElements[0].name;
            this.selectionType = selectedElements[0].type;
          } else {
            this.selection = selectedElements;
            this.selectionType = "Multi-Select";
          }
        }
      } catch (err) {
        this.$bvModal.hide("modal-selector");
        this.resetModal();
        console.error(err);
        this.handleError(err, this, this.instanceId);
      } finally {
        this.showOverlay = false;
      }
    },

    handleOk(event) {
      this.checkValid = true;
      event.preventDefault();
      this.handleSubmit();
    },

    handleSubmit() {
      if (this.$v.selection.$invalid) {
        this.$message({
          type: "warning",
          message: "Make a selection before clicking OK.",
          duration: 7000,
          showClose: true,
        });
        return;
      }
      if (this.mappingType === "Attribute") {
        this.$nextTick(() => {
          this.$bvModal.hide("modal-selector");
        });
        this.$emit("selectionSubmitted", {
          type: this.type,
          index: this.index,
          display:
            this.selectionType === "Multi-Select"
              ? this.selection.map((el) => el.name).join(", ")
              : this.selection,
          selection: this.attribute,
          hierarchy: this.hierarchy,
          selectionType: "Attribute",
          selectionAlias: null,
        });
        this.resetModal();
        return;
      }
      if (this.mappingType === "Subset") {
        this.$nextTick(() => {
          this.$bvModal.hide("modal-selector");
        });
        this.$emit("selectionSubmitted", {
          type: this.type,
          index: this.index,
          display:
            this.selectionType === "Multi-Select"
              ? this.selection.map((el) => el.name).join(", ")
              : this.selection,
          selection: this.subset,
          hierarchy: this.hierarchy,
          selectionType: "Subset",
          selectionAlias: null,
        });
        this.resetModal();
        return;
      }
      if (this.placeholder && this.selection === this.placeholder.selection) {
        this.$nextTick(() => {
          this.$bvModal.hide("modal-selector");
        });
        this.$emit("selectionSubmitted", {
          type: this.type,
          index: this.index,
          display:
            this.selectionType === "Multi-Select"
              ? this.selection.map((el) => el.name).join(", ")
              : this.selection,
          hierarchy: this.hierarchy,
          selection: null,
          selectionType: null,
          selectionAlias: null,
        });
        this.resetModal();
        return;
      }

      // else if (this.$v.selectionType.$invalid) {
      //   if (this.type.includes("target") && this.taskSourceType !== "Value") {
      //     if (this.placeholder && this.placeholder.selectionType === "String") {
      //       this.$message({
      //         type: "warning",
      //         message: "Select a string element.",
      //         duration: 7000,
      //         showClose: true,
      //       });
      //     } else {
      //       this.$message({
      //         type: "warning",
      //         message:
      //           "Select a base level element, or select the same element as the source.",
      //         duration: 7000,
      //         showClose: true,
      //       });
      //     }
      //   }
      //   return;
      // }
      this.$nextTick(() => {
        this.$bvModal.hide("modal-selector");
      });
      this.$emit("selectionSubmitted", {
        type: this.type,
        index: this.index,
        selection: this.selection,
        hierarchy: this.hierarchy,
        selectionType: this.selectionType,
        selectionAlias: this.alias,
      });
      this.resetModal();
    },
    async subsetSelected() {
      if (this.mappingType === "Subset") {
        this.selection = this.subset;
        this.selectionType = "Subset";
      } else {
        this.loadSubsetElements();
      }
    },
    async hierarchySelected() {
      this.loadElements();
    },
  },
  validations() {
    return {
      selection: { required },
      selectionType: {
        required,
        isNotConsolidated(value) {
          let result = true;
          if (this.type.includes("target") && this.taskSourceType !== "Value") {
            if (
              this.placeholder &&
              this.placeholder.selectionType === "String"
            ) {
              result = value === "String";
            } else {
              result = value === "Numeric";
            }
          }
          return result;
        },
      },
    };
  },
};
</script>

<style scoped>
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.cancel-button {
  background-color: #7a7c80;
  color: white;
  border-color: white;
}
.cancel-button:focus {
  background-color: #606266;
  color: white;
  border-color: white;
  outline: none;
}
.cancel-button:hover {
  background-color: #606266;
  color: white;
  border-color: white;
  outline: none;
}
.fw {
  width: calc(100% / 3);
  display: inline-flex;
  flex-direction: column;
}
</style>